/**
 * Portfolio-local presentation helpers (Story UI-U7).
 *
 * The UI FORMATS money it is given as strings; it never computes (rule #2). A gated /
 * absent money value renders the honest em dash `—`, never `CHF 0` (the V10-Q precedent).
 *
 * [Source: apps/web/src/lib/format.ts]
 */

import type { LineOfBusiness, PraemienMonatDto } from '@brokly/shared';
import { chf } from '../../lib/format';

/** Client-side range slice for the Prämienentwicklung chart: keep the last `months` points. */
export function sliceSeries(series: PraemienMonatDto[], months: number): PraemienMonatDto[] {
  return series.slice(-months);
}

/** Money string (or null when gated/absent) → 'CHF 12'400.00' or the honest dash. */
export function chfOrDash(v: string | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  return Number.isFinite(n) ? chf(n) : '-';
}

/** German Sparten labels (V10-Q precedent). null / unknown → 'Übrige'. */
const SPARTE_LABELS: Record<string, string> = {
  uvg: 'UVG',
  ktg: 'KTG',
  bu: 'BU',
  leben: 'Leben',
  sach: 'Sach',
  haftpflicht: 'Haftpflicht',
  kranken: 'Kranken',
  other: 'Übrige',
};

export function sparteLabel(s: LineOfBusiness | null): string {
  if (!s) return 'Übrige';
  return SPARTE_LABELS[s] ?? 'Übrige';
}

/** Relative/short time for the activity feed: 'vor 5 Min.', 'vor 3 Std.', 'vor 2 T.', else date. */
export function relTimeDe(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMin = Math.round((Date.now() - then) / 60000);
  if (diffMin < 1) return 'gerade eben';
  if (diffMin < 60) return `vor ${diffMin} Min.`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `vor ${diffH} Std.`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 7) return `vor ${diffD} T.`;
  return new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
